import React, { useState, useEffect, useCallback } from 'react';
import { Users, UserPlus, Crown, Edit3, Eye, Shield, X, Check, Mail, ChevronDown } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { toast } from '../hooks/use-toast';
import api from '../utils/api';

const ROLE_CONFIG = {
  owner: { label: 'Owner', color: 'bg-amber-500/20 text-amber-400 border-amber-500/30', icon: Crown },
  admin: { label: 'Admin', color: 'bg-blue-500/20 text-blue-400 border-blue-500/30', icon: Shield },
  editor: { label: 'Editor', color: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30', icon: Edit3 },
  viewer: { label: 'Viewer', color: 'bg-slate-500/20 text-muted-foreground border-slate-500/30', icon: Eye },
};

const STATUS_CONFIG = {
  accepted: { label: 'Active', color: 'text-emerald-400' },
  pending: { label: 'Pending', color: 'text-amber-400' },
};

const inputClass = "bg-background border-input text-foreground placeholder:text-muted-foreground focus:border-blue-500/50";

const TeamPanel = ({ project }) => {
  const [members, setMembers] = useState([]);
  const [isOwner, setIsOwner] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('editor');
  const [inviting, setInviting] = useState(false);
  const [showRoleMenu, setShowRoleMenu] = useState(null);

  const fetchTeam = useCallback(async () => {
    if (!project?.id) return;
    try {
      const res = await api.get(`/projects/${project.id}/team`);
      setMembers(res.data.members || []);
      setIsOwner(res.data.is_owner);
    } catch { /* ignore */ }
  }, [project?.id]);

  useEffect(() => { fetchTeam(); }, [fetchTeam]);

  const handleInvite = async () => {
    if (!inviteEmail.trim()) return;
    setInviting(true);
    try {
      await api.post(`/projects/${project.id}/team/invite`, { email: inviteEmail.trim().toLowerCase(), role: inviteRole });
      toast({ title: 'Invitation Sent', description: `Invited ${inviteEmail} as ${inviteRole}` });
      setInviteEmail('');
      await fetchTeam();
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || 'Failed to invite', variant: 'destructive' });
    } finally { setInviting(false); }
  };

  const handleChangeRole = async (memberId, newRole) => {
    try {
      await api.put(`/projects/${project.id}/team/${memberId}/role`, { role: newRole });
      toast({ title: 'Role Updated' });
      setShowRoleMenu(null);
      await fetchTeam();
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || 'Failed to update role', variant: 'destructive' });
    }
  };

  const handleRemove = async (memberId, name) => {
    try {
      await api.delete(`/projects/${project.id}/team/${memberId}`);
      toast({ title: 'Member Removed', description: `${name} has been removed from the project` });
      await fetchTeam();
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || 'Failed to remove member', variant: 'destructive' });
    }
  };

  return (
    <div className="h-full flex flex-col bg-background overflow-y-auto">
      <div className="p-5 space-y-5">
        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-violet-600/20 rounded-xl flex items-center justify-center">
            <Users className="w-5 h-5 text-violet-400" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-foreground">Team</h2>
            <p className="text-xs text-muted-foreground">{members.length} member{members.length !== 1 ? 's' : ''}</p>
          </div>
        </div>

        {/* Invite Section */}
        {isOwner && (
          <Card className="bg-card border-border">
            <CardContent className="p-4 space-y-3">
              <div className="flex items-center gap-2">
                <UserPlus className="w-4 h-4 text-blue-400" />
                <span className="text-sm text-foreground font-medium">Invite Member</span>
              </div>
              <div className="flex gap-2">
                <Input value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder="teammate@email.com" type="email"
                  onKeyDown={(e) => e.key === 'Enter' && handleInvite()}
                  className={`flex-1 text-sm ${inputClass}`} data-testid="invite-email-input" />
                <select value={inviteRole} onChange={(e) => setInviteRole(e.target.value)}
                  className="bg-muted border border-input text-foreground rounded-md px-3 text-sm" data-testid="invite-role-select">
                  <option value="admin">Admin</option>
                  <option value="editor">Editor</option>
                  <option value="viewer">Viewer</option>
                </select>
              </div>
              <Button onClick={handleInvite} disabled={inviting || !inviteEmail.trim()}
                className="w-full bg-blue-600 hover:bg-blue-500 gap-2" data-testid="send-invite-btn">
                {inviting ? 'Sending...' : <><Mail className="w-4 h-4" /> Send Invitation</>}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Members List */}
        <div className="space-y-2" data-testid="team-members-list">
          {members.map(member => {
            const roleConfig = ROLE_CONFIG[member.role] || ROLE_CONFIG.viewer;
            const RoleIcon = roleConfig.icon;
            const statusConfig = STATUS_CONFIG[member.status] || STATUS_CONFIG.pending;

            return (
              <div key={member.id} className="flex items-center gap-3 p-3 bg-card rounded-xl border border-border hover:border-input transition-colors"
                data-testid={`team-member-${member.id}`}>
                {/* Avatar */}
                <div className="w-9 h-9 rounded-full bg-accent flex items-center justify-center text-sm font-bold text-muted-foreground shrink-0 overflow-hidden">
                  {member.avatar ? (
                    <img src={member.avatar} alt="" className="w-full h-full object-cover" />
                  ) : (
                    member.name?.charAt(0)?.toUpperCase() || member.email?.charAt(0)?.toUpperCase() || '?'
                  )}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-foreground font-medium truncate">{member.name}</span>
                    {member.status === 'pending' && (
                      <span className={`text-xs ${statusConfig.color}`}>({statusConfig.label})</span>
                    )}
                  </div>
                  <span className="text-xs text-muted-foreground truncate block">{member.email}</span>
                </div>

                {/* Role Badge */}
                <div className="relative">
                  <button onClick={() => isOwner && !member.is_owner && setShowRoleMenu(showRoleMenu === member.id ? null : member.id)}
                    className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg border text-xs font-medium ${roleConfig.color} ${isOwner && !member.is_owner ? 'cursor-pointer hover:opacity-80' : 'cursor-default'}`}
                    data-testid={`member-role-${member.id}`}>
                    <RoleIcon className="w-3 h-3" />
                    {roleConfig.label}
                    {isOwner && !member.is_owner && <ChevronDown className="w-3 h-3" />}
                  </button>

                  {/* Role Dropdown */}
                  {showRoleMenu === member.id && (
                    <div className="absolute right-0 top-full mt-1 bg-muted border border-input rounded-lg shadow-xl z-50 w-36 overflow-hidden">
                      {['admin', 'editor', 'viewer'].map(r => (
                        <button key={r} onClick={() => handleChangeRole(member.id, r)}
                          className={`w-full text-left px-3 py-2 text-xs hover:bg-accent transition-colors flex items-center gap-2 ${member.role === r ? 'text-blue-400' : 'text-foreground/80'}`}>
                          {React.createElement(ROLE_CONFIG[r].icon, { className: 'w-3 h-3' })}
                          {ROLE_CONFIG[r].label}
                          {member.role === r && <Check className="w-3 h-3 ml-auto" />}
                        </button>
                      ))}
                      <div className="border-t border-border">
                        <button onClick={() => handleRemove(member.id, member.name)}
                          className="w-full text-left px-3 py-2 text-xs text-red-400 hover:bg-red-500/10 flex items-center gap-2">
                          <X className="w-3 h-3" /> Remove
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Role Legend */}
        <div className="p-4 bg-card rounded-xl border border-border">
          <h4 className="text-xs font-semibold text-muted-foreground mb-2 uppercase tracking-wider">Role Permissions</h4>
          <div className="space-y-2">
            {[
              { role: 'Admin', desc: 'Full access — can invite members, edit, deploy' },
              { role: 'Editor', desc: 'Can chat with AI, edit code, and view all content' },
              { role: 'Viewer', desc: 'Read-only access to project content and chat history' }
            ].map(item => (
              <div key={item.role} className="flex gap-2 text-xs">
                <span className="text-foreground/80 font-medium w-14 shrink-0">{item.role}</span>
                <span className="text-muted-foreground">{item.desc}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TeamPanel;
